import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  nextTick,
} from 'vue';
import ResponsiveSize from '@/classes/ResponsiveSize';


export default /*#__PURE__*/_defineComponent({
  __name: 'LazyBackgroundMedia',
  props: {
  mediaSizes: {
    type: [String, Object],
    default: null,
  },
  backgroundPositionY: {
    type: String,
    default: null,
  },
  backgroundPositionX: {
    type: String,
    default: null,
  },
  mediaMobileSizes: {
    type: String,
    default: () => Buffer.from('null', 'utf-8').toString('base64'),
  },
  backgroundPositionMobileY: {
    type: String,
    default: 'same',
  },
  backgroundPositionMobileX: {
    type: String,
    default: 'same',
  },
},
  setup(__props) {

const props = __props;



const root = ref<InstanceType<typeof HTMLElement>>();

const src = ref<string>('none');
const isMobile = ref<boolean>(false);

const getMediaSourcesObject = (propMs: unknown): Array<{ width: number, url: string }> => {
  let ms;

  if (typeof propMs === 'string') {
    try {
      ms = Object.values(JSON.parse(atob((propMs as string) || btoa('{}'))));
    } catch {
      ms = {};
    }
  } else {
    ms = Object.values(propMs as Record<string, { width: number, url: string }>);
  }

  return Object.values(ms || {}) as Array<{ width: number, url: string }>;
};

const mediaSources = computed<Iterable<{ width: number, url: string }>>(() => {
  let ms = getMediaSourcesObject(props.mediaSizes);
  const msMobile = getMediaSourcesObject(props.mediaMobileSizes);

  if (isMobile.value && msMobile.length) {
    ms = msMobile;
  }

  return ms.sort((
    m1: { width: number },
    m2: { width: number },
  ) => (m1.width < m2.width ? -1 : 1));
});

const setBackgroundImage = () => {
  src.value = 'none';
  isMobile.value = ResponsiveSize.isMobileBreakpoint();

  nextTick(() => {
    if (root.value) {
      const possibleSources = Object.values([...mediaSources.value]).filter((msrc) => {
        if (root.value && msrc.width >= root.value.offsetWidth) {
          return true;
        }

        return false;
      });

      if (possibleSources.length) {
        src.value = possibleSources[0].url;
      } else if (Object.values(mediaSources.value).length) {
        src.value = Object.values(mediaSources.value)[Object.values(mediaSources.value).length - 1].url;
      }
    }
  });
};

const setEventHandler = () => {
  window.addEventListener('resize', setBackgroundImage);
  window.addEventListener('load', setBackgroundImage);
  setBackgroundImage();
};

const handleIntersect = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
): void => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      setEventHandler();
      if (root.value) {
        observer.unobserve(root.value);
      }
    }
  });
};

const createObserver = (): void => {
  const observer = new IntersectionObserver(handleIntersect, {
    root: null,
    threshold: 0,
  });

  if (root.value) {
    observer.observe(root.value);
  }
};

const backgroundStyles = computed(() => {
  let styles = `background-image: url('${src.value}');`;
  let posX = props.backgroundPositionX;
  let posY = props.backgroundPositionY;

  if (isMobile.value) {
    if (props.backgroundPositionMobileX !== 'same') {
      posX = props.backgroundPositionMobileX;
    }
    if (props.backgroundPositionMobileY !== 'same') {
      posY = props.backgroundPositionMobileY;
    }
  }

  if (['left', 'center', 'right'].indexOf(posX) >= 0) {
    styles += `background-position-x: ${posX};`;
  }

  if (['top', 'center', 'bottom'].indexOf(posY) >= 0) {
    styles += `background-position-y: ${posY};`;
  }

  return styles;
});

onMounted(() => {
  if (window.IntersectionObserver) {
    createObserver();
  } else {
    setEventHandler();
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', setBackgroundImage);
  window.removeEventListener('load', setBackgroundImage);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "io-lazy-background",
    ref_key: "root",
    ref: root
  }, [
    (src.value !== 'none')
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          style: _normalizeStyle(backgroundStyles.value)
        }, null, 4))
      : _createCommentVNode("", true)
  ], 512))
}
}

})